<script lang="ts">
    import Configurator from "./routes/Configurator.svelte"
    import MyOrders from "./routes/MyOrders.svelte"
    import MyOrdersRequest from "./routes/MyOrdersRequest.svelte"
    import Checkout from "./routes/Checkout.svelte"
    import Search from "./routes/Search.svelte"
    import { spaNavigate } from "../functionTools/navigation"
    import { getCachedEntries } from "../api"
    import { initialLocale, locales } from "../i18n"
    import { onMount } from "svelte"
    import Footer from "./layout/global/Footer.svelte"
    import {
        navigationIdToPathMap,
        overlay,
        setNavigationIdToPathMap,
        setNavigationPathToIdMap,
        staticFrontendLinks,
    } from "../store"
    import { location } from "../i18n"
    import { scrollToTop } from "svelte-scrollto"
    import Preview from "./routes/Preview.svelte"
    import Content from "./routes/Content.svelte"
    import Header from "./layout/global/header/Header.svelte"
    import SidebarOverlay from "./widgets/SidebarOverlay.svelte"
    import Product from "./routes/Product.svelte"
    import OrderRating from "./routes/OrderRating.svelte"
    import { iframeView, theme } from "../functionTools/iframeIntegration"
    import Notifications from "./widgets/Notifications.svelte"
    import ConsentBanner from "./consent/ConsentBanner.svelte"
    import { login } from "../functionTools/login"
    import { baseURL } from "../configs/config"
    import QrEntry from "./ecommerce/qr/QrEntry.svelte"
    import { isAnalyticsGranted, loadGtag, trackPageView } from "../lib/ga"

    interface Props {
        url?: string
    }

    let { url = "" }: Props = $props()

    if (url) {
        // ssr
        let l = url.split("?")
        const actualURL = baseURL + url
        $location = {
            url: actualURL,
            path: l[0],
            search: l.length > 1 ? l[1] : "",
            hash: "",
            push: false,
            pop: false,
        }
    }

    let oldPath: string
    if (typeof window !== "undefined") {
        // Gespeicherter Consent (Reload/Folgeseite): gtag laden + genau einen page_view.
        // Ohne Locale-Präfix (Rewrite nach /{locale} folgt) noch nichts feuern —
        // sonst zählt / und /de doppelt.
        try {
            const p = window.location.pathname
            const hasLocale = p === `/${initialLocale}` || p.startsWith(`/${initialLocale}/`)
            if (isAnalyticsGranted() && hasLocale) {
                loadGtag()
                trackPageView()
            }
        } catch {
            // ignore
        }
    }
    location.subscribe((l) => {
        if (oldPath !== undefined && oldPath !== l.path) {
            // Pfadwechsel = page_view (push UND popstate/back-forward).
            // Consent-Gate + Pfad-Guard liegen in trackPageView; Pfad aus dem
            // Store (window.location hinkt dem Proxy hinterher).
            try {
                trackPageView(l.path + (l.search || "") + (l.hash || ""))
            } catch {
                // ignore
            }
            scrollToTop()
            // Fokus zurücksetzen bei Navigation
            if (typeof window !== "undefined") {
                setTimeout(() => {
                    // Entferne Fokus von aktuellem Element
                    const activeElement = document.activeElement as HTMLElement
                    if (activeElement && activeElement !== document.body) {
                        activeElement.blur()
                    }
                    // Setze Fokus auf Body und entferne ihn sofort wieder
                    document.body.setAttribute("tabindex", "-1")
                    document.body.focus()
                    document.body.removeAttribute("tabindex")
                }, 100)
            }
        }
        oldPath = l.path
    })

    // Fokus auf Overlay setzen wenn es geöffnet wird
    overlay.subscribe((overlayState) => {
        if (overlayState?.show && typeof window !== "undefined") {
            setTimeout(() => {
                const overlayElement = document.querySelector(".sidebar-overlay") as HTMLElement
                if (overlayElement) {
                    // Suche nach dem ersten fokussierbaren Element im Overlay
                    const focusableElements = overlayElement.querySelectorAll(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                    )
                    if (focusableElements.length > 0) {
                        ;(focusableElements[0] as HTMLElement).focus()
                    } else {
                        // Falls kein fokussierbares Element, fokussiere das Overlay selbst
                        overlayElement.setAttribute("tabindex", "-1")
                        overlayElement.focus()
                    }
                }
            }, 100)
        }
    })

    // ESC-Taste schließt Overlays und Space-Taste verhindert Scrollen bei fokussierten Elementen
    $effect(() => {
        if (typeof window === "undefined") return

        const handleKeyDown = (event: KeyboardEvent) => {
            // ESC schließt Overlays
            if (event.key === "Escape" && $overlay?.show) {
                overlay.set({ ...$overlay, show: false })
            }

            // Verhindere Scrollen mit Space bei fokussierten interaktiven Elementen
            if (event.key === " " || event.code === "Space") {
                const activeElement = document.activeElement as HTMLElement
                if (
                    activeElement &&
                    (activeElement.tagName === "BUTTON" ||
                        activeElement.tagName === "A" ||
                        activeElement.tagName === "INPUT" ||
                        activeElement.tagName === "TEXTAREA" ||
                        activeElement.tagName === "SELECT" ||
                        activeElement.getAttribute("role") === "button" ||
                        activeElement.hasAttribute("tabindex"))
                ) {
                    // Lasse Space bei Inputs/Textareas zu (zum Tippen)
                    if (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA") {
                        return
                    }
                    // Verhindere Standard-Scroll-Verhalten
                    event.preventDefault()
                    // Triggere Click für Buttons und Links
                    if (activeElement.tagName === "BUTTON" || activeElement.tagName === "A") {
                        activeElement.click()
                    }
                }
            }
        }

        window.addEventListener("keydown", handleKeyDown)

        return () => {
            window.removeEventListener("keydown", handleKeyDown)
        }
    })

    if (typeof window !== "undefined") {
        onMount(() => {
            let path = $location?.path
            let included = false
            locales.forEach((locale) => {
                if (path.startsWith(`/${locale}`)) included = true
            })
            if (included) return

            if (path.endsWith("/")) path = path.slice(0, -1)
            const localePattern = /^\/(de|en|dk|fr|se)(\/|$)/
            const match = localePattern.exec(path)
            if (match) {
                const locale = match[1]
                const rest = path.slice(locale.length + 1)
                if (locale === "de") return
                const newPath = rest ? (rest.startsWith("/") ? rest : `/${rest}`) : ""
                spaNavigate(`/de${newPath}`)
                return
            }
            // garkeine locale
            spaNavigate(`/${initialLocale}${path}`)
        })
    }
    let loading = $state(typeof window == "undefined")

    const staticLinksPromise = getCachedEntries("staticFrontendLink").then((res) => {
        const links = (res || []) as StaticFrontendLink[]
        const csfl = $staticFrontendLinks
        links.forEach((link: any) => {
            const linkType = link.type as string
            // @ts-ignore
            csfl[linkType] = {
                de: "",
                en: "",
                fr: "",
                se: "",
                dk: "",
                // @ts-ignore
                ...(csfl[linkType] || {}),
                [link.lang]: link.page,
            }
        })
        staticFrontendLinks.set(csfl)
    })

    const navigationPromise = getCachedEntries("navigation").then((entries) => {
        setNavigationIdToPathMap(entries as any)
    })

    const contentPromise = getCachedEntries(
        "content",
        { "generalDetails.lang": initialLocale },
        null,
        null,
        null,
        "navigation"
    ).then((entries) => {
        setNavigationPathToIdMap(entries as any)
    })

    Promise.all([staticLinksPromise, navigationPromise, contentPromise])
        .catch((err) => {
            console.error("Fehler beim Laden der Daten:", err)
        })
        .finally(() => {
            loading = false
        })

    theme.subscribe((theme) => {
        if (typeof document !== "undefined") {
            document.querySelector("html")?.setAttribute("data-theme", theme)
        }
    })

    $effect(() => {
        if ($login) {
            if (typeof window !== "undefined") {
                const dealerLoginMessage = sessionStorage.getItem("dealerLoginMessage")
                if (!dealerLoginMessage) {
                    $overlay = {
                        show: true,
                        type: "dealerLoginMessage",
                        properties: {},
                    }
                }
            }
        }
    })
    const productPathRegex = /^\/?([^\/]+\/)*[^_/]+_/

    // metrics
    let _oldPath = $state("")
    if (typeof window !== "undefined") {
        $effect(() => {
            const _newPath = $location.path + $location.search
            if (_oldPath !== _newPath) {
                const ref = _oldPath
                    ? document.location.protocol + "//" + document.location.host + _oldPath
                    : document.referrer
                _oldPath = _newPath
                const fetchPath = _oldPath + (_oldPath.includes("?") ? "&" : "?") + "metrics"
                fetch(fetchPath, {
                    method: "POST",
                    headers: {
                        "content-type": "application/json",
                        "x-ssr-skip": "204",
                        "x-ssr-ref": ref,
                        "x-ssr-res": `${window.screen?.width}x${window.screen?.height}`,
                        // no cache
                        "cache-control": "no-cache, no-store, must-revalidate",
                    },
                    body: "{}",
                })
            }
        })
    }
</script>

{#if !loading}
    {#if !$iframeView}
        <Header />
    {/if}
    <div id="main-content" tabindex="-1" class="skip-target"></div>
    <main>
        {#key $location?.path}
            {#if typeof window !== "undefined" && (window.location?.pathname?.includes(`/${initialLocale}/preview`) || window.location?.pathname?.includes(`/preview`))}
                <Preview />
            {:else if $location?.path?.startsWith(`/${initialLocale}/product`)}
                <Product locationPath={$location?.path} />
            {:else if $location?.path?.startsWith(`/${initialLocale}/checkout`)}
                <Checkout />
            {:else if $location?.path?.startsWith(`/${initialLocale}/search`)}
                {#key $location?.search}
                    <Search />
                {/key}
            {:else if $location?.path?.startsWith(`/${initialLocale}/order/rating/`)}
                <OrderRating params={$location?.path.split(`/${initialLocale}/order/rating/`).pop()} />
            {:else if $location?.path?.startsWith(`/${initialLocale}/order/`) && $location?.path?.split("/order").length == 2}
                <MyOrders token={$location?.path?.split("/order/")[1].split("/").shift()} />
            {:else if $location?.path?.startsWith(`/${initialLocale}/order`)}
                <MyOrdersRequest />
            {:else if $location?.path?.startsWith(`/${initialLocale}/brief-und-paketkaesten/konfigurator`)}
                <Configurator />
            {:else if $location?.path?.match(`/(${initialLocale}/)?qr`)}
                <QrEntry />
            {:else if productPathRegex.test($location?.path || "")}
                <Product locationPath={$location?.path} />
            {:else if Object.values($staticFrontendLinks).length > 0 && Object.keys($navigationIdToPathMap).length > 0}
                <Content location={$location} />
            {/if}
        {/key}
    </main>
    {#if !$iframeView}
        <Footer />
    {:else}
        <div class="iframe-powered">
            Powered by <img src="media/renz-logo.png" alt="Renz Shop" />
            <button
                class="button transparent"
                onclick={(e) => {
                    e.preventDefault()
                    $overlay = {
                        show: true,
                        type: "content",
                        properties: {
                            // @ts-ignore
                            pageToRender: $staticFrontendLinks["imprint"][initialLocale],
                        },
                    }
                }}
            >
                Impressum
            </button>
            <button
                class="button transparent"
                onclick={(e) => {
                    e.preventDefault()
                    $overlay = {
                        show: true,
                        type: "content",
                        properties: {
                            // @ts-ignore
                            pageToRender: $staticFrontendLinks["dataProtection"][initialLocale],
                        },
                    }
                }}
            >
                Datenschutz
            </button>
        </div>
    {/if}
    <SidebarOverlay />
    <Notifications />
    <ConsentBanner />
{/if}

<style lang="less" global>
    @import "../css/main.less";
    @import "../css/webfont.less";

    main {
        width: 100%;

        max-width: var(--max-page-width);
    }

    .iframe-powered {
        position: absolute;
        top: 0;
        right: 0;
        font-size: 0.7em;
        padding: 5px 10px;

        img {
            height: 18px;
            width: auto;
            vertical-align: middle;
            margin-left: 5px;
        }

        button {
            color: inherit;
            text-decoration: underline;
            margin-left: 10px;
        }
    }
</style>
